/**
 * Agreements module — co-parenting agreements. One parent PROPOSES a written
 * agreement (title + body); the OTHER parent ACCEPTS or DECLINES it. The proposer
 * may WITHDRAW their own proposal before a response. Once a status leaves
 * `proposed` the record is LOCKED (no further actions) — like the message log,
 * an accepted agreement is a permanent, court-usable record.
 *
 * Parents-only feature (gated by the registry `scope: 'parent'` + ModuleGuard);
 * trusted members never see it.
 */
import { useMemo, useState } from 'react';
import type { Agreement, AgreementStatus } from '@solandia/core';
import { ApiClientError, proposeAgreementInputSchema } from '@solandia/core';
import { useI18n } from '../../i18n';
import type { Dictionary } from '../../i18n/sk';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { makePeopleResolver } from '../../family/people';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

const STATUS_TONE: Record<AgreementStatus, 'primary' | 'success' | 'danger' | 'default'> = {
  proposed: 'primary',
  accepted: 'success',
  declined: 'danger',
  withdrawn: 'default',
};

function statusLabel(status: AgreementStatus, t: Dictionary): string {
  return t.agreements.status[status];
}

export function AgreementsModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, me } = useFamily();

  const { data, loading, error, reload } = useAsync<Agreement[]>(async () => {
    const res = await api.listAgreements(family.id);
    return res.items;
  }, [family.id]);

  const people = useMemo(
    () => makePeopleResolver(me, t.agreements.you),
    [me, t.agreements.you],
  );

  // Newest first (server already orders, but keep it deterministic client-side).
  const ordered = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [data],
  );

  const [proposing, setProposing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(
    id: string,
    fn: (id: string) => Promise<Agreement>,
  ) {
    setBusyId(id);
    setActionError(null);
    try {
      await fn(id);
      reload();
    } catch (err) {
      // 409 = already locked, 403 = wrong actor — both resolve by reloading the
      // (now stale) list so the user sees the up-to-date state.
      if (err instanceof ApiClientError && (err.status === 409 || err.status === 403)) {
        setActionError(t.agreements.actionStale);
        reload();
      } else {
        setActionError(t.errors.generic);
      }
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <SectionHeader
        title={t.agreements.title}
        subtitle={t.agreements.lead}
        action={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setProposing(true)}
          >
            ＋ {t.agreements.propose}
          </button>
        }
      />

      {actionError ? (
        <p className="field-error" role="alert" style={{ marginBottom: 12 }}>
          {actionError}
        </p>
      ) : null}

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
      {ordered.length === 0 && !loading ? (
        <EmptyState
          emoji="🤝"
          title={t.agreements.empty}
          hint={t.agreements.emptyLead}
        />
      ) : null}

      <ul className="list">
        {ordered.map((a) => {
          const iAmProposer = people.isMe(a.proposedById);
          const isProposed = a.status === 'proposed';
          const busy = busyId === a.id;
          return (
            <li key={a.id} className="list-item">
              <div className="grow stack-sm">
                <div className="row wrap">
                  <strong>{a.title}</strong>
                  <Badge tone={STATUS_TONE[a.status]}>{statusLabel(a.status, t)}</Badge>
                </div>
                <p className="message-body" style={{ whiteSpace: 'pre-wrap' }}>
                  {a.body}
                </p>
                <span className="text-xs muted">
                  {t.agreements.proposedBy}: {people.label(a.proposedById)} ·{' '}
                  <time dateTime={a.createdAt}>
                    {new Date(a.createdAt).toLocaleString('sk-SK')}
                  </time>
                </span>
                {!isProposed && a.respondedById && a.respondedAt ? (
                  <span className="text-xs muted">
                    {t.agreements.respondedBy}: {people.label(a.respondedById)} ·{' '}
                    <time dateTime={a.respondedAt}>
                      {new Date(a.respondedAt).toLocaleString('sk-SK')}
                    </time>
                  </span>
                ) : null}
                {!isProposed ? (
                  <span className="text-xs muted">{t.agreements.locked}</span>
                ) : null}
              </div>

              {isProposed ? (
                <div className="stack-sm" style={{ alignItems: 'flex-end' }}>
                  {iAmProposer ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => act(a.id, (id) => api.withdrawAgreement(id))}
                      disabled={busy}
                    >
                      {busy ? t.common.saving : t.agreements.withdraw}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => act(a.id, (id) => api.acceptAgreement(id))}
                        disabled={busy}
                      >
                        {busy ? t.common.saving : t.agreements.accept}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => act(a.id, (id) => api.declineAgreement(id))}
                        disabled={busy}
                      >
                        {t.agreements.decline}
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {proposing ? (
        <ProposeAgreementDialog
          onClose={() => setProposing(false)}
          onCreated={() => {
            setProposing(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function ProposeAgreementDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<{ title?: string; body?: string }>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = proposeAgreementInputSchema.safeParse({
      title: title.trim(),
      body: body.trim(),
    });
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        title: fieldErrors.title ? t.common.required : undefined,
        body: fieldErrors.body ? t.common.required : undefined,
      });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      await api.proposeAgreement(family.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ title: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.agreements.proposeTitle} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <p className="text-sm muted">{t.agreements.proposeLead}</p>
        <Field label={t.agreements.titleLabel} error={errors.title}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={title}
              aria-invalid={invalid}
              maxLength={160}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.agreements.titlePlaceholder}
              autoFocus
            />
          )}
        </Field>
        <Field label={t.agreements.bodyLabel} error={errors.body}>
          {({ id, invalid }) => (
            <textarea
              id={id}
              className="textarea"
              value={body}
              aria-invalid={invalid}
              maxLength={8000}
              rows={6}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t.agreements.bodyPlaceholder}
            />
          )}
        </Field>
        <p className="text-xs muted">{t.agreements.proposeNote}</p>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.agreements.propose}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
